import Styles from './BlogPostPreviewChrome.module.scss'
import { Close } from 'blaze-ui/icons'
import Blog from 'almanac-ui/icons/blaze/24/Blog'
import {
  ExternalIntegration,
  ExternalIntegrationEnum,
  WordPressProfile,
  WixProfile,
  ShopifyProfile,
} from 'api/clients/ExternalIntegrationsClient'
import { getBlogPlatformBrandingWithOnboardingSelection } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { ChannelType } from 'components/CampaignCreationModal/Types'

const Lock = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9 4H8.5V3C8.5 1.62 7.38 0.5 6 0.5C4.62 0.5 3.5 1.62 3.5 3V4H3C2.45 4 2 4.45 2 5V10C2 10.55 2.45 11 3 11H9C9.55 11 10 10.55 10 10V5C10 4.45 9.55 4 9 4ZM4.5 4V3C4.5 2.17 5.17 1.5 6 1.5C6.83 1.5 7.5 2.17 7.5 3V4H4.5Z"
      fill="black"
      fillOpacity="0.6"
    />
  </svg>
)

const CurveLineLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="6" height="8" viewBox="0 0 6 8" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 8C1.38462 7.05882 4.61538 6.58824 6 0C6 0 6 8 6 8H0Z"
      fill="white"
    />
  </svg>
)

const CurveLineRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="6" height="8" viewBox="0 0 6 8" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6 8C4.61538 7.05882 1.38462 6.58824 0 0C0 0 0 8 0 8H6Z"
      fill="white"
    />
  </svg>
)

interface BlogPostPreviewChromeProps {
  externalIntegration?: ExternalIntegration
}

export const BlogPostPreviewChrome = ({ externalIntegration }: BlogPostPreviewChromeProps) => {
  const goHighLevelEnabled = useFlipperFlag('go_high_level_integration')
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const shopifyEnabled = useFlipperFlag('shopify_integration')
  const blogPlatformOnboardingBrandingEnabled = useFlipperFlag('blog_platform_onboarding_branding')
  const { connectedBlogIntegration } = useBlogIntegrations()
  const { data: brandPlan } = useCurrentBrandPlan()

  const enhancedBlogPlatform = getBlogPlatformBrandingWithOnboardingSelection({
    channel: ChannelType.BlogPost,
    externalIntegrations: connectedBlogIntegration ? [connectedBlogIntegration] : [],
    selectedBlogPlatform: brandPlan?.selected_blog_platform,
    featureFlags: {
      goHighLevelEnabled,
      wixEnabled,
      blogPlatformOnboardingBrandingEnabled,
      shopifyEnabled,
    },
  })

  const finalPlatform = enhancedBlogPlatform || externalIntegration?.provider

  let label = 'WordPress Blog'
  if (finalPlatform === ExternalIntegrationEnum.GoHighLevel) {
    label = 'GoHighLevel Blog'
  } else if (finalPlatform === ExternalIntegrationEnum.Wix) {
    label = 'Wix Blog'
  } else if (finalPlatform === ExternalIntegrationEnum.Shopify) {
    label = 'Shopify Blog'
  }

  let url = 'blog.wordpress.com'
  if (finalPlatform === ExternalIntegrationEnum.GoHighLevel) {
    url = externalIntegration?.name || 'blog.gohighlevel.com'
  } else if (finalPlatform === ExternalIntegrationEnum.Wix) {
    url = (externalIntegration?.additional_data as WixProfile)?.site_url || 'blog.wix.com'
  } else if (finalPlatform === ExternalIntegrationEnum.Shopify) {
    const selectedBlog = (externalIntegration?.additional_data as ShopifyProfile)?.shopify_blogs?.find(
      (blog) => blog.id === (externalIntegration?.additional_data as ShopifyProfile)?.selected_blog_id,
    )
    url = selectedBlog?.handle
      ? `${(externalIntegration?.additional_data as ShopifyProfile)?.domain}/blogs/${selectedBlog?.handle}`
      : 'blog.shopify.com'
  } else {
    url = (externalIntegration?.additional_data as WordPressProfile)?.url || 'blog.wordpress.com'
  }

  return (
    <div className={Styles.blogPostChromeContainer}>
      <div className={Styles.tabContainer}>
        <div className={Styles.circles}>
          <div className={Styles.littleCircle}></div>
          <div className={Styles.littleCircle}></div>
          <div className={Styles.littleCircle}></div>
        </div>
        <div className={Styles.tab}>
          <CurveLineLeft />
          <div className={Styles.tabContent}>
            <Blog size={16} blogPlatform={enhancedBlogPlatform} />
            <div className={Styles.text}>{label}</div>
            <Close size={16} />
          </div>
          <CurveLineRight />
        </div>
      </div>
      <div className={Styles.urlContainer}>
        <div className={Styles.url}>
          <Lock />
          <div className={Styles.text}>{url}</div>
        </div>
        <div className={Styles.circle}></div>
      </div>
    </div>
  )
}
